// api/payments.ts
import axios from "axios";
import { AXIOS_CONFIG } from "../api";
import { backend_url } from "@/constants";

const API_URL = `${backend_url}/api/stripe/management`;

export const createStripeIntegration = async (content = {}) => {
  try {
    const response = await axios.post(
      `${API_URL}/integration/`,
      content, // Optional body data
      {
        ...AXIOS_CONFIG,
      }
    );
    // Adjust return value based on how your API responds:
    return response.data;
  } catch (error) {
    console.error("Error creating Stripe integration info:", error);
    throw error;
  }
};

export const getStripeOnboardingStatus = async () => {
  try {
    const response = await axios.get(`${API_URL}/integration/`, {
      ...AXIOS_CONFIG,
    });
    // Adjust return value based on how your API responds:
    return response.data;
  } catch (error) {
    console.error("Error fetching Stripe onboarding status:", error);
    throw error;
  }
};

export const getStripeDashboardUrl = async () => {
  try {
    const response = await axios.post(
      `${API_URL}/dashboard/`,
      {},
      {
        ...AXIOS_CONFIG,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching Stripe dashboard URL:", error);
    throw error;
  }
};

/**
 * Fetches a paginated list of payment requests.
 * @param {object} params - Query parameters for filtering payments.
 * @param {number} [params.client_id] - (Optional) Filter payments by client ID.
 * @param {string} [params.payment_status] - (Optional) Filter by payment status (e.g., "Pending").
 * @param {number} [params.limit=10] - Number of results to return per request.
 * @param {number} [params.offset=0] - Number of results to skip for pagination.
 * @returns {Promise<object>} The list of payment requests.
 */
export const getPaymentRequests = async (params = {}) => {
  try {
    const response = await axios.get(`${API_URL}/payments/`, {
      ...AXIOS_CONFIG,
      params,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Error retrieving payment requests:",
        error.response?.data || error.message
      );
    } else {
      console.error("Error retrieving payment requests:", error);
    }
    throw error;
  }
};

/**
 * Creates a new payment request for a client.
 * @param {object} data - The payment request data.
 * @param {number} data.client_id - ID of the client.
 * @param {number} data.amount - Amount in cents.
 * @param {string} data.description - Description of the payment request.
 * @returns {Promise<object>} The created payment request details.
 */
export const createPaymentRequest = async (data: {
  client_id: string;
  amount: number;
  description: string;
}) => {
  try {
    const response = await axios.post(`${API_URL}/payments/`, data, {
      ...AXIOS_CONFIG,
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Error creating payment request:",
        error.response?.data || error.message
      );
    } else {
      console.error("Error creating payment request:", error);
    }
    throw error;
  }
};

/**
 * Creates a new Stripe Checkout session for a specific payment request.
 * @param {string} paymentRequestId - The ID of the PaymentRequestModel.
 * @returns {Promise<object>} The checkout session data, including the checkout URL.
 */
export const createCheckoutSession = async (paymentRequestId: string) => {
  try {
    const response = await axios.post(
      `${API_URL}/checkouts/`,
      { payment_request_id: paymentRequestId },
      {
        ...AXIOS_CONFIG,
        headers: {
          ...AXIOS_CONFIG.headers,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Error creating checkout session:",
        error.response?.data || error.message
      );
    } else {
      console.error("Error creating checkout session:", error);
    }
    throw error;
  }
};

/**
 * Creates a refund for a give checkout session.
 * @param {string} payment_request_id - The ID of the PaymentRequestModel.
 * @returns {Promise<object>} The checkout session data, including the checkout URL.
 */

export const createRefund = async (payment_request_id: string) => {
  try {
    const response = await axios.post(
      `${API_URL}/refunds/`,
      { payment_request_id: payment_request_id },
      {
        ...AXIOS_CONFIG,
        headers: {
          ...AXIOS_CONFIG.headers,
          "Content-Type": "application/json",
        },
      }
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      console.error(
        "Error creating refund:",
        error.response?.data || error.message
      );
    } else {
      console.error("Error creating refund:", error);
    }
    throw error;
  }
};

export const getStripeProductId = async () => {
  try {
    const response = await axios.get(`${API_URL}/webhooks/`, {
      ...AXIOS_CONFIG,
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching Stripe product ID:", error);
    throw error;
  }
};
